import React, { useEffect, useState } from 'react'
import {
  searchAddressByPostcode,
  formatAddressForDisplay,
} from '../../api/getAddress'

const AddressSection = ({
  userInfo,
  setUserInfo,
  profileData,
  setProfileData,
  showTitle = true,
  dependant = false,
}) => {
  const [isSearching, setIsSearching] = useState(false)
  const [addresses, setAddresses] = useState([])
  const [showAddresses, setShowAddresses] = useState(false)
  const [postcodeSearch, setPostcodeSearch] = useState('')
  const [searchError, setSearchError] = useState('')

  const handlePostcodeSearch = async () => {
    const postcode = postcodeSearch.trim()

    if (!postcode) {
      setSearchError('Please enter a postcode')
      return
    }

    setIsSearching(true)
    setSearchError('')
    setShowAddresses(false)

    try {
      const foundAddresses = await searchAddressByPostcode(postcode)

      setAddresses(foundAddresses)
      setShowAddresses(foundAddresses.length > 0)

      if (foundAddresses.length === 0) {
        setSearchError('No addresses found for this postcode')
      }
    } catch (error) {
      setSearchError(error.message)
      setAddresses([])
      setShowAddresses(false)
    } finally {
      setIsSearching(false)
    }
  }

  const handleAddressSelect = (selectedAddress) => {
    setUserInfo((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        addressLine1: selectedAddress.line_1,
        addressLine2: selectedAddress.line_2 || '',
        city: selectedAddress.town_or_city,
        county: selectedAddress.county,
        country: selectedAddress.country,
        postcode: selectedAddress.postcode,
      },
    }))
    setShowAddresses(false)
    setAddresses([])
  }

  const handleClearAddress = () => {
    setUserInfo((prev) => ({
      ...prev,
      address: {
        ...prev.address,
        addressLine1: '',
        addressLine2: '',
        city: '',
        county: '',
        country: '',
      },
    }))
    setAddresses([])
    setShowAddresses(false)
    setSearchError('')
  }
  return (
    <>
      {showTitle && (
        <div className="sm:col-span-6">
          {dependant ? (
          <h2 className="text-xl font-medium">Dependant Address</h2>
          ) : (
          <h2 className="text-xl font-medium">Your Address</h2>

          )}
        </div>
      )}

      <div className="sm:col-span-6">
        <label htmlFor="postcode" className="block text-sm/6 font-medium">
          Search via Postcode
        </label>
        <div className="flex items-end gap-2">
          <input
            type="text"
            placeholder="Postcode"
            onChange={(e) => setPostcodeSearch(e.target.value)}
            className="mt-2 block w-1/2 rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
          />
          <div className="w-1/2 flex items-center space-x-2">
            <button
              type="button"
              onClick={handlePostcodeSearch}
              disabled={isSearching}
              className="text-sm inline-flex justify-center items-center cursor-pointer bg-teal-500 border-teal-500 hover:bg-teal-600 disabled:bg-teal-300 disabled:cursor-not-allowed text-white font-bold py-2 px-4 border rounded-lg"
            >
              {isSearching ? 'Searching...' : 'Search'}
            </button>
          </div>
        </div>

        {/* Error message */}
        {searchError && (
          <div className="mt-2 text-sm text-red-600">{searchError}</div>
        )}

        {/* Address dropdown */}
        {showAddresses && addresses.length > 0 && (
          <div className="mt-2 relative">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Select an address:
            </label>
            <div className="max-h-48 overflow-y-auto border border-gray-300 rounded-md bg-white shadow-lg">
              {addresses.map((address) => (
                <button
                  key={address.id}
                  type="button"
                  onClick={() => handleAddressSelect(address)}
                  className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 focus:outline-none focus:bg-blue-50"
                >
                  <div className="text-sm text-gray-900">
                    {formatAddressForDisplay(address)}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        <label htmlFor="address" className="block text-sm/6 font-medium mt-6">
          Address
        </label>
        <input
          type="text"
          placeholder="Address Line 1"
          value={userInfo?.address?.addressLine1 || ''}
          onChange={(e) =>
            setUserInfo((prev) => ({
              ...prev,
              address: {
                ...prev.address,
                addressLine1: e.target.value,
              },
            }))
          }
          className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
        />
        <input
          type="text"
          placeholder="Address Line 2"
          value={userInfo?.address?.addressLine2 || ''}
          onChange={(e) =>
            setUserInfo((prev) => ({
              ...prev,
              address: {
                ...prev.address,
                addressLine2: e.target.value,
              },
            }))
          }
          className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
        />
        <div className="grid grid-cols-8 gap-2">
          <input
            type="text"
            placeholder="Town/City"
            value={userInfo?.address?.city || ''}
            onChange={(e) =>
              setUserInfo((prev) => ({
                ...prev,
                address: {
                  ...prev.address,
                  city: e.target.value,
                },
              }))
            }
            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base col-span-2 border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
          />

          <input
            type="text"
            placeholder="Postcode"
            value={userInfo?.address?.postcode || ''}
            onChange={(e) =>
              setUserInfo((prev) => ({
                ...prev,
                address: {
                  ...prev.address,
                  postcode: e.target.value,
                },
              }))
            }
            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base col-span-2 border border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
          />

          <input
            type="text"
            placeholder="Country"
            value={userInfo?.address?.country || ''}
            onChange={(e) =>
              setUserInfo((prev) => ({
                ...prev,
                address: {
                  ...prev.address,
                  country: e.target.value,
                },
              }))
            }
            className="mt-2 block w-full rounded-md bg-white px-3 py-1.5 text-base border col-span-4 border-gray-300 placeholder:text-gray-400 focus:border-violet-300 focus:ring-1 focus:ring-violet-300 focus:outline-none sm:text-sm/6"
          />
        </div>
      </div>
    </>
  )
}

export default AddressSection
